import { useCallback, useEffect, useRef, useState } from 'react';
import { _isNil, _notNil } from '@/littledash';
import type { AgentStreamEventV1, AgentStreamEventType } from '@/model/Agent.model';
import { TERMINAL_STREAM_EVENTS } from '@/model/Agent.model';

export interface UseAgentStreamOptions {
  sessionApiId: string | null;
  /**
   * When set, only events for this execution are routed; events for other executions on the same
   * session (e.g. an earlier execution's terminal replayed on connect) are ignored and do NOT tear
   * the stream down. Leave undefined to route every event on the session.
   */
  executionApiId?: string | null;
  onEvent: (event: AgentStreamEventV1) => void;
  onError: (error: string) => void;
  onTerminal: (eventType: AgentStreamEventType) => void;
}

export interface UseAgentStreamOutput {
  isConnected: boolean;
  disconnect: () => void;
}

/**
 * Subscribes to the agent harness SSE stream for a single session. Routes each
 * event into the supplied callbacks and closes the connection on a terminal
 * stream event so the caller does not need to manage teardown.
 */
export const useAgentStream = ({
  sessionApiId,
  executionApiId,
  onEvent,
  onError,
  onTerminal,
}: UseAgentStreamOptions): UseAgentStreamOutput => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);
  const onTerminalRef = useRef(onTerminal);
  const executionFilterRef = useRef(executionApiId);

  useEffect(() => {
    onEventRef.current = onEvent;
    onErrorRef.current = onError;
    onTerminalRef.current = onTerminal;
    executionFilterRef.current = executionApiId;
  });

  const disconnect = useCallback(() => {
    if (_notNil(eventSourceRef.current)) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    if (_isNil(sessionApiId)) {
      return;
    }

    const url = new URL(
      `/api/messaging/v1/ai-agents/sessions/${sessionApiId}/stream`,
      window.AppConfig.messagingApiUrl
    );
    const es = new EventSource(url.href, { withCredentials: true });
    eventSourceRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
    };

    const handleEvent = (messageEvent: MessageEvent): void => {
      let parsed: AgentStreamEventV1;
      try {
        parsed = JSON.parse(messageEvent.data) as AgentStreamEventV1;
      } catch {
        onErrorRef.current(`Failed to parse SSE event: ${messageEvent.data}`);
        return;
      }

      if (messageEvent.lastEventId !== '') {
        parsed.id = messageEvent.lastEventId;
      }

      // Ignore events for a different execution (e.g. an earlier execution's terminal replayed when
      // this connection opens). Such events must not reach the caller or tear the stream down.
      const executionFilter = executionFilterRef.current;
      if (_notNil(executionFilter) && parsed.execution_api_id !== executionFilter) {
        return;
      }

      onEventRef.current(parsed);

      if (TERMINAL_STREAM_EVENTS.has(parsed.type)) {
        onTerminalRef.current(parsed.type);
        disconnect();
      }
    };

    const eventTypes: Array<AgentStreamEventType> = [
      'execution_started',
      'thinking',
      'phase_transition',
      'tool_call_start',
      'tool_call_end',
      'text_delta',
      'text_done',
      'completed',
      'failed',
      'cancelled',
      'subagent_started',
      'subagent_completed',
      'subagent_failed',
    ];
    eventTypes.forEach((eventType) => es.addEventListener(eventType, handleEvent));

    es.onerror = () => {
      if (eventSourceRef.current !== es) {
        return;
      }
      // EventSource fires onerror both for transient blips (the messaging server restarting or a
      // network hiccup — readyState CONNECTING, the browser is already auto-retrying) and for
      // permanent closure (readyState CLOSED). Only a permanent close is fatal. For a transient
      // error we keep the EventSource open and let the browser reconnect on its own; because the
      // server sets an `id` on every frame and replays from `Last-Event-ID`, the stream resumes
      // exactly where it left off with no missed events. We only drop `isConnected` so the UI can
      // show a "reconnecting" state.
      if (es.readyState === EventSource.CLOSED) {
        onErrorRef.current('SSE connection error');
        disconnect();
        return;
      }
      setIsConnected(false);
    };

    return () => {
      disconnect();
    };
  }, [sessionApiId, disconnect]);

  return { isConnected, disconnect };
};
